/**
 * Auth store — current user + login/logout.
 * JWT is persisted via services/http.ts setToken/getToken (localStorage).
 * Email is also persisted so the route guard passes synchronously on refresh
 * (no flash of /login while bootstrap awaits /v1/auth/me).
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { auth as authApi } from '@/services/api';
import { ApiError, getToken, setToken } from '@/services/http';

const EMAIL_KEY = 'rounds_user_email_v1';
function readPersistedEmail(): string | null {
  try { return localStorage.getItem(EMAIL_KEY); } catch { return null; }
}
function writePersistedEmail(v: string | null): void {
  try {
    if (v) localStorage.setItem(EMAIL_KEY, v);
    else localStorage.removeItem(EMAIL_KEY);
  } catch { /* private mode */ }
}

/** Read the `sub` (email) claim from a JWT without verifying. The server just
 * issued the token on a successful POST /login, so the login path can take the
 * email from here instead of a second GET /me round-trip — one less request to
 * hang on during a backend restart. bootstrap() still validates persisted
 * tokens via /me on app start. */
function jwtSub(token: string): string | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: string };
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch { return null; }
}

export const useAuthStore = defineStore('auth', () => {
  const email = ref<string | null>(readPersistedEmail());
  // Phase 2 S2 (2026-07-20): auth_users.role from GET /v1/auth/me — the
  // backend has honored roles since 2f9e45b; this lets the UI show admin
  // affordances to role-admins. Not persisted (revalidated per session);
  // null until bootstrap resolves or for env-CSV fallback identities.
  const role = ref<string | null>(null);
  // True once a /v1/auth/me has settled this session (bootstrap OR the
  // post-login background fetch below), success or failure. Consumers use it
  // to avoid showing a "read-only" affordance before the role is even known —
  // otherwise a role-admin who signs in via the form sees a false read-only
  // flash until the background /me lands. Reset on logout. Not persisted.
  const roleKnown = ref(false);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  // True once the persisted token has been validated (or there is none). The
  // route guard awaits this before allowing a protected route, and App.vue
  // shows a splash until it flips — so protected UI never paints before the
  // token is verified (fixes the /dashboard→/login flash).
  const ready = ref(false);

  const isAuthenticated = computed(() => Boolean(email.value && getToken()));

  async function bootstrap(): Promise<void> {
    // Snapshot the token we're about to validate. If login() swaps in a fresh
    // token while this /me is in flight, we must NOT clear that new token when
    // the stale /me comes back 401 — that race is exactly why login appeared to
    // need 3-4 attempts (boot-time validation of an expired token clobbered the
    // just-issued session). /me uses noAuthHandling so http() also leaves the
    // token + the page alone here; bootstrap owns the clear decision.
    const validating = getToken();
    if (!validating) return;
    try {
      const me = await authApi.me();
      email.value = me.email;
      role.value = me.role ?? null;
      writePersistedEmail(me.email);
    } catch (e) {
      // Token bad — clear it, but only if it's still the token we validated.
      // A concurrent login() may have already replaced it with a good one.
      if (getToken() === validating) {
        setToken(null);
        email.value = null;
        role.value = null;
        writePersistedEmail(null);
      }
    } finally {
      // We attempted the role lookup; role is now known (either the real value
      // or known-to-be-null after a failure). Gates the read-only affordances.
      roleKnown.value = true;
    }
  }

  // Validate the persisted token at most once per session. Memoized so the
  // guard can await it on every navigation at zero cost after the first.
  // With no token we resolve synchronously (logged-out users get no /me call
  // and no splash); otherwise we await bootstrap() and flip `ready` when done.
  let bootstrapPromise: Promise<void> | null = null;
  function ensureBootstrapped(): Promise<void> {
    if (!bootstrapPromise) {
      if (!getToken()) {
        ready.value = true;
        bootstrapPromise = Promise.resolve();
      } else {
        bootstrapPromise = bootstrap().finally(() => { ready.value = true; });
      }
    }
    return bootstrapPromise;
  }

  async function login(emailInput: string, password: string): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      const resp = await authApi.login(emailInput.trim().toLowerCase(), password);
      setToken(resp.access_token);
      // Take the email from the JWT the server just issued — no redundant
      // GET /me on the login hot path (removes a request that could hang
      // during a backend restart and freeze the post-login redirect).
      const who = jwtSub(resp.access_token) ?? emailInput.trim().toLowerCase();
      email.value = who;
      writePersistedEmail(who);
      // Settle the token-validation memo: the token is freshly minted by the
      // server, so the route guard's ensureBootstrapped() on the post-login
      // redirect resolves instantly — it neither waits on nor can be undone by
      // an in-flight boot-time /me. This is what makes login land first-try.
      ready.value = true;
      bootstrapPromise = Promise.resolve();
      // Fetch the role in the BACKGROUND so admin affordances appear without a
      // manual reload. Deliberately NOT awaited — the post-login redirect must
      // never wait on /me (preserves the first-try login fix 7ee1a5a). The
      // token is freshly minted, so a failure here leaves it untouched; we
      // just leave role null (UI stays conservative) and mark it known. Before
      // this, role stayed null for the whole session after a form login, so
      // every role-admin except the legacy email saw a false "read-only".
      roleKnown.value = false;
      void authApi.me()
        .then((me) => { role.value = me.role ?? null; roleKnown.value = true; })
        .catch(() => { roleKnown.value = true; });
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        error.value = 'Incorrect email or password';
      } else {
        error.value = e instanceof Error ? e.message : 'Login failed';
      }
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  function logout(): void {
    setToken(null);
    email.value = null;
    // Clear the role too, or a stale role='admin' leaks into the next login on
    // the same tab and shows admin affordances to a non-admin (the server still
    // 403s the writes, but the UX would lie until the next /me settles).
    role.value = null;
    roleKnown.value = false;
    writePersistedEmail(null);
  }

  return { email, role, roleKnown, isLoading, error, ready, isAuthenticated, bootstrap, ensureBootstrapped, login, logout };
});
